/**
 * Display currency (₪ / $ / €). Checkout always charges the store currency; USD and EUR are an approximate
 * display converted here from the cents in every <span class="money" data-money>. Elements with
 * data-money-text hold a longer string (e.g. "From ₪1,700"): only that money part is swapped.
 * Rates: European Central Bank daily rates (api.frankfurter.dev), cached for a day; theme settings are the fallback.
 * Default currency comes from the visitor's time zone (Israel ₪, Europe €, elsewhere $); a choice is remembered.
 */
(() => {
  const config = window.themeCurrency;
  if (!config) return;

  const CHOICE_KEY = 'kc-currency';
  const RATES_KEY = 'kc-rates';
  const DAY = 864e5;
  const supported = [config.base, 'USD', 'EUR'];
  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        /* private mode: the choice lasts for this page only */
      }
    },
  };

  function defaultCurrency() {
    let zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (error) {
      return config.base;
    }
    if (!zone || zone === 'Asia/Jerusalem' || zone === 'Asia/Tel_Aviv') return config.base;
    return zone.startsWith('Europe/') ? 'EUR' : 'USD';
  }

  let currency = storage.get(CHOICE_KEY);
  if (!supported.includes(currency)) currency = defaultCurrency();

  // Units of each currency per 1 store-currency unit.
  let rates = { USD: 1 / config.fallback.USD, EUR: 1 / config.fallback.EUR };
  try {
    const cached = JSON.parse(storage.get(RATES_KEY) || 'null');
    if (cached && Date.now() - cached.time < DAY && cached.base === config.base) rates = cached.rates;
  } catch (error) {
    /* ignore a broken cache */
  }

  const lang = document.documentElement.lang === 'he' ? 'he-IL' : 'en-US';
  const formatters = {};
  const format = (amount, code) => {
    formatters[code] =
      formatters[code] ||
      new Intl.NumberFormat(lang, { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return formatters[code].format(amount);
  };

  function render(el) {
    if (el.dataset.moneyOriginal === undefined) el.dataset.moneyOriginal = el.textContent;
    const original = el.dataset.moneyOriginal;
    if (currency === config.base || !rates[currency]) {
      if (el.textContent !== original) el.textContent = original;
      return;
    }
    // Shown without an approximate sign; the cart note says $/€ prices are approximate.
    const converted = format((Number(el.dataset.money) / 100) * rates[currency], currency);
    const text = el.dataset.moneyText ? original.replace(el.dataset.moneyText, converted) : converted;
    if (el.textContent !== text) el.textContent = text;
  }

  function apply(scope = document) {
    scope.querySelectorAll('[data-money]').forEach(render);
    scope.querySelectorAll('[data-currency-note]').forEach((note) => (note.hidden = currency === config.base));
    if (scope === document) {
      document.querySelectorAll('[data-currency-switch]').forEach((group) => {
        group.hidden = false;
        const select = group.querySelector('[data-currency-select]');
        if (select) select.value = currency;
      });
    }
  }

  async function loadRates() {
    try {
      const cached = JSON.parse(storage.get(RATES_KEY) || 'null');
      if (cached && Date.now() - cached.time < DAY && cached.base === config.base) return;
    } catch (error) {
      /* refetch */
    }
    try {
      const response = await fetch(`https://api.frankfurter.dev/v1/latest?base=${config.base}&symbols=USD,EUR`);
      if (!response.ok) return;
      const data = await response.json();
      if (!data.rates?.USD || !data.rates?.EUR) return;
      rates = { USD: data.rates.USD, EUR: data.rates.EUR };
      storage.set(RATES_KEY, JSON.stringify({ time: Date.now(), base: config.base, rates }));
      apply();
    } catch (error) {
      /* offline or blocked: keep the fallback rates */
    }
  }

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-currency-select]')) return;
    currency = event.target.value;
    storage.set(CHOICE_KEY, currency);
    apply();
  });

  // Prices re-rendered later (cart, variant change, filters, recommendations) are converted as they arrive.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) =>
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches('[data-money]')) render(node);
        apply(node);
      })
    );
  }).observe(document.body, { childList: true, subtree: true });

  apply();
  loadRates();
})();
